// skipcq: JS-W1028
import { useCallback, useMemo, useRef, useState } from 'react';
import useBeerStyles from './useBeerStyles';
import useBeerStyleTypes from './useBeerStyleTypes';
import { buildCatalogStyleMapping } from '../utils/beerStyle/catalogStyleMapping';
import {
  bulkApproveIngestion,
  bulkRejectIngestion,
  applyIngestionReview,
  completeIngestionReview,
  decideIngestionCandidate,
} from '../services/beerIngestionService';
import {
  captureBeerIngestionReviewAcknowledge,
  captureBeerIngestionReviewApply,
  captureBeerIngestionReviewApprove,
  captureBeerIngestionReviewBulk,
  captureBeerIngestionReviewBulkReject,
  captureBeerIngestionReviewComplete,
  captureBeerIngestionReviewEdit,
  captureBeerIngestionReviewError,
  captureBeerIngestionReviewFilter,
  captureBeerIngestionReviewReject,
  captureBeerIngestionReviewStyleTypeAssign,
} from '../utils/beerIngestionAnalytics';
import { COMPARISON_FILTER_APPLY_ERRORS } from '../utils/brewery/beerIngestionComparison';
import { beerNameIdentityKey } from '../utils/brewery/beerNameIdentityKey';
import {
  countReviewQueueFromCandidates,
  duplicateApprovedNameCaption,
  filterReviewCandidates,
  hasUnresolvedApplyErrors,
  initialReviewCandidateFilter,
  isBulkEligibleReviewCandidate,
  isExtractedStyleMapped,
  isIngestionApplyRetryable,
  isIngestionReviewApplied,
  isIngestionReviewLocked,
  isRejectableReviewCandidate,
  isReviewDraftEdited,
  isReviewRowFrozen,
  mappedStyleNamesForBulk,
  proposedBeerDraftFromCandidate,
  resolveProposedStyleForReview,
  resolveProposedStyleType,
  snapshotBeerForCandidate,
  styleTypeCaptionForLabel,
  unknownExtractedStyles,
  unmappedApprovedStyleNames,
  unmappedApprovedStylesCopy,
} from '../utils/brewery/beerIngestionReview';

const EMPTY_REVIEW_DECISIONS = {};

function isReviewMutationInFlight(
  actionInFlightRef,
  decisionInFlightKeyRef,
  styleAssignInFlightRef,
) {
  return actionInFlightRef.current
    || Boolean(decisionInFlightKeyRef.current)
    || styleAssignInFlightRef.current;
}

function toProposedBeer(draft, sessionStyleTypes, styleMapping, beerStyleIds) {
  const abvRaw = typeof draft.abv === 'string' ? draft.abv.trim() : '';
  const abv = abvRaw === '' ? null : Number(abvRaw);
  const styleLabel = draft.style?.trim() || null;
  const resolved = resolveProposedStyleForReview(styleLabel, styleMapping, beerStyleIds);
  return {
    name: draft.name?.trim() || null,
    style: resolved.style,
    abv: Number.isFinite(abv) ? abv : null,
    description: draft.description?.trim() || null,
    styleType: resolveProposedStyleType(
      styleLabel,
      resolved.isCatalogStyle,
      sessionStyleTypes,
      styleMapping,
    ),
  };
}

export default function useBeerIngestionReviewWorkspace({
  run,
  snapshot,
  comparison,
  review,
  breweryId,
  onReviewUpdated,
  onApplySuccess,
}) {
  const { beerStyles, createBeerStyle } = useBeerStyles();
  const { styleTypes } = useBeerStyleTypes();
  const styleMapping = useMemo(
    () => buildCatalogStyleMapping(beerStyles),
    [beerStyles],
  );
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState({});
  const [editingKeys, setEditingKeys] = useState({});
  const [sessionStyleTypes, setSessionStyleTypes] = useState({});
  const [drafts, setDrafts] = useState({});
  const [actionError, setActionError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [loadingCandidateKey, setLoadingCandidateKey] = useState(null);
  const [loadingDecisionAction, setLoadingDecisionAction] = useState('');
  const [applying, setApplying] = useState(false);
  const [assigningStyleName, setAssigningStyleName] = useState(null);
  const [assigningStyleType, setAssigningStyleType] = useState('');
  const [applyResult, setApplyResult] = useState(null);
  const actionInFlightRef = useRef(false);
  const decisionInFlightKeyRef = useRef(null);
  const styleAssignInFlightRef = useRef(false);

  const scopedApplyResult = applyResult?.runId === run?.id ? applyResult : null;
  const activeReview = scopedApplyResult?.review ?? review;

  const decisions = useMemo(
    () => activeReview?.decisions ?? EMPTY_REVIEW_DECISIONS,
    [activeReview],
  );
  const candidates = useMemo(
    () => (Array.isArray(comparison?.candidates) ? comparison.candidates : []),
    [comparison],
  );
  const snapshotBeers = useMemo(
    () => (Array.isArray(snapshot?.beers) ? snapshot.beers : []),
    [snapshot],
  );
  const beerStyleIds = useMemo(
    () => new Set((beerStyles || []).map((styleRow) => styleRow.id)),
    [beerStyles],
  );
  const reviewLocked = isIngestionReviewLocked(activeReview, run);
  const reviewApplied = isIngestionReviewApplied(activeReview);
  const applyBlocked = run?.applyStatus === 'applied';
  const applyStatus = activeReview?.applyStatus ?? run?.applyStatus ?? null;
  const applyRetryable = !applyBlocked && isIngestionApplyRetryable(applyStatus);
  const reviewCompleted = activeReview?.reviewStatus === 'completed'
    || run?.reviewStatus === 'completed';
  const approvedCount = Number(activeReview?.approvedCount ?? review?.approvedCount) || 0;
  const importResults = scopedApplyResult?.importResults ?? null;

  const unmappedApprovedStyles = useMemo(
    () => unmappedApprovedStyleNames(
      candidates,
      decisions,
      styleMapping,
      beerStyleIds,
      sessionStyleTypes,
    ),
    [beerStyleIds, candidates, decisions, sessionStyleTypes, styleMapping],
  );
  const unknownStyles = useMemo(
    () => {
      if (reviewApplied) {
        return [];
      }
      if (reviewLocked) {
        return unmappedApprovedStyles;
      }
      return unknownExtractedStyles(
        candidates,
        styleMapping,
        beerStyleIds,
        sessionStyleTypes,
      );
    },
    [
      beerStyleIds,
      candidates,
      reviewApplied,
      reviewLocked,
      sessionStyleTypes,
      styleMapping,
      unmappedApprovedStyles,
    ],
  );
  const unmappedStylesMessage = unmappedApprovedStylesCopy(
    unmappedApprovedStyles,
    reviewCompleted ? 'apply' : 'complete',
  );

  const defaultFilter = initialReviewCandidateFilter(activeReview, run);
  const filter = selectedFilter ?? defaultFilter;

  const visibleCandidates = useMemo(
    () => filterReviewCandidates(candidates, filter, decisions),
    [candidates, decisions, filter],
  );

  const bulkEligibleKeys = useMemo(() => {
    if (reviewLocked) {
      return [];
    }
    const seenNameKeys = new Set();
    return visibleCandidates
      .filter((candidate) => {
        const snapshotBeer = snapshotBeerForCandidate(candidate, snapshotBeers);
        const styleMapped = isExtractedStyleMapped(
          candidate.currentNormalized?.style,
          styleMapping,
          beerStyleIds,
          sessionStyleTypes,
        );
        const draft = drafts[candidate.candidateKey]
          ?? proposedBeerDraftFromCandidate(candidate);
        const nameBlocked = Boolean(duplicateApprovedNameCaption(
          candidate.candidateKey,
          draft?.name,
          decisions,
        ));
        const eligible = isBulkEligibleReviewCandidate(candidate, snapshotBeer, styleMapped)
          && !decisions[candidate.candidateKey]
          && !isReviewDraftEdited(draft, candidate)
          && !nameBlocked;
        if (!eligible) {
          return false;
        }
        const nameKey = beerNameIdentityKey(draft?.name);
        if (nameKey && seenNameKeys.has(nameKey)) {
          return false;
        }
        if (nameKey) {
          seenNameKeys.add(nameKey);
        }
        return true;
      })
      .map((candidate) => candidate.candidateKey);
  }, [
    beerStyleIds,
    decisions,
    drafts,
    reviewLocked,
    sessionStyleTypes,
    snapshotBeers,
    styleMapping,
    visibleCandidates,
  ]);

  const rejectableKeys = useMemo(() => {
    if (reviewLocked) {
      return [];
    }
    return visibleCandidates
      .filter((candidate) => (
        isRejectableReviewCandidate(candidate, decisions[candidate.candidateKey])
      ))
      .map((candidate) => candidate.candidateKey);
  }, [decisions, reviewLocked, visibleCandidates]);

  const selectableKeys = useMemo(() => {
    const keys = new Set([...bulkEligibleKeys, ...rejectableKeys]);
    return visibleCandidates
      .map((candidate) => candidate.candidateKey)
      .filter((candidateKey) => keys.has(candidateKey));
  }, [bulkEligibleKeys, rejectableKeys, visibleCandidates]);

  const selectedCount = useMemo(
    () => selectableKeys.filter((candidateKey) => selectedKeys[candidateKey]).length,
    [selectableKeys, selectedKeys],
  );
  const selectedBulkEligibleCount = useMemo(
    () => bulkEligibleKeys.filter((candidateKey) => selectedKeys[candidateKey]).length,
    [bulkEligibleKeys, selectedKeys],
  );
  const selectedRejectableCount = useMemo(
    () => rejectableKeys.filter((candidateKey) => selectedKeys[candidateKey]).length,
    [rejectableKeys, selectedKeys],
  );
  const headerCheckboxState = useMemo(() => {
    if (selectableKeys.length === 0) {
      return { checked: false, indeterminate: false };
    }
    if (selectedCount === selectableKeys.length) {
      return { checked: true, indeterminate: false };
    }
    if (selectedCount > 0) {
      return { checked: false, indeterminate: true };
    }
    return { checked: false, indeterminate: false };
  }, [selectableKeys, selectedCount]);

  const queueCounts = useMemo(() => {
    if (!candidates.length) {
      const storedPending = Number(run?.reviewPendingCount ?? review?.pendingCount) || 0;
      const storedDecided = Number(run?.reviewDecidedCount) || (
        (review?.approvedCount || 0)
        + (review?.rejectedCount || 0)
        + (review?.acknowledgedCount || 0)
      );
      return { pendingCount: storedPending, decidedCount: storedDecided };
    }
    return countReviewQueueFromCandidates(candidates, decisions);
  }, [candidates, decisions, review, run]);
  const pendingCount = queueCounts.pendingCount;
  const decidedCount = queueCounts.decidedCount;

  const handleFilterChange = useCallback((unusedEvent, nextFilter) => {
    if (!nextFilter || !run) {
      return;
    }
    setSelectedFilter(nextFilter);
    captureBeerIngestionReviewFilter(run.id, breweryId, nextFilter);
  }, [breweryId, run]);

  const handleToggleSelected = useCallback((event) => {
    const candidateKey = event.currentTarget.getAttribute('data-candidate-key');
    if (!candidateKey) {
      return;
    }
    setSelectedKeys((current) => ({
      ...current,
      [candidateKey]: !current[candidateKey],
    }));
  }, []);

  const handleSelectEligible = useCallback(() => {
    const next = {};
    bulkEligibleKeys.forEach((candidateKey) => {
      next[candidateKey] = true;
    });
    setSelectedKeys(next);
  }, [bulkEligibleKeys]);

  const handleToggleSelectAll = useCallback(() => {
    if (selectableKeys.length === 0) {
      return;
    }
    const shouldClear = selectedCount === selectableKeys.length;
    if (shouldClear) {
      setSelectedKeys((current) => {
        const next = { ...current };
        selectableKeys.forEach((candidateKey) => {
          next[candidateKey] = false;
        });
        return next;
      });
      return;
    }
    setSelectedKeys((current) => {
      const next = { ...current };
      selectableKeys.forEach((candidateKey) => {
        next[candidateKey] = true;
      });
      return next;
    });
  }, [selectableKeys, selectedCount]);

  const handleDraftChange = useCallback((event) => {
    const candidateKey = event.currentTarget.getAttribute('data-candidate-key');
    const field = event.currentTarget.getAttribute('data-field');
    if (!candidateKey || !field) {
      return;
    }
    const value = event.currentTarget.value;
    setDrafts((current) => ({
      ...current,
      [candidateKey]: {
        ...(current[candidateKey] ?? proposedBeerDraftFromCandidate(
          candidates.find((candidate) => candidate.candidateKey === candidateKey),
        )),
        [field]: value,
      },
    }));
  }, [candidates]);

  const handleToggleRowEdit = useCallback((event) => {
    const candidateKey = event.currentTarget.getAttribute('data-candidate-key');
    if (!candidateKey || reviewLocked || isReviewRowFrozen(decisions[candidateKey])) {
      return;
    }
    const nextEditing = !editingKeys[candidateKey];
    setEditingKeys((current) => ({
      ...current,
      [candidateKey]: nextEditing,
    }));
    captureBeerIngestionReviewEdit(run?.id, breweryId, candidateKey, nextEditing);
  }, [breweryId, decisions, editingKeys, reviewLocked, run]);

  const handleAssignStyleType = useCallback(async (event) => {
    const styleName = event.target.name;
    const styleType = event.target.value;
    if (
      !styleName
      || !styleType
      || reviewLocked
      || isReviewMutationInFlight(
        actionInFlightRef,
        decisionInFlightKeyRef,
        styleAssignInFlightRef,
      )
    ) {
      return;
    }
    styleAssignInFlightRef.current = true;
    setActionError(null);
    setAssigningStyleName(styleName);
    setAssigningStyleType(styleType);
    try {
      await createBeerStyle(styleName, styleType);
      setSessionStyleTypes((current) => ({ ...current, [styleName]: styleType }));
      captureBeerIngestionReviewStyleTypeAssign(run?.id, breweryId, styleName, styleType);
    } catch (mappingError) {
      setActionError(mappingError?.message || 'Failed to save style mapping');
    } finally {
      styleAssignInFlightRef.current = false;
      setAssigningStyleName(null);
      setAssigningStyleType('');
    }
  }, [breweryId, createBeerStyle, reviewLocked, run]);

  const persistDecision = useCallback(async (candidateKey, action) => {
    if (
      !run
      || reviewLocked
      || isReviewMutationInFlight(
        actionInFlightRef,
        decisionInFlightKeyRef,
        styleAssignInFlightRef,
      )
    ) {
      return;
    }
    const candidate = candidates.find((item) => item.candidateKey === candidateKey);
    if (isReviewRowFrozen(decisions[candidateKey])) {
      return;
    }
    const draft = drafts[candidateKey] ?? proposedBeerDraftFromCandidate(candidate);
    decisionInFlightKeyRef.current = candidateKey;
    setLoadingCandidateKey(candidateKey);
    setLoadingDecisionAction(action);
    setActionError(null);
    try {
      const result = await decideIngestionCandidate({
        runId: run.id,
        candidateKey,
        action,
        proposedBeer: toProposedBeer(draft, sessionStyleTypes, styleMapping, beerStyleIds),
      });
      if (action === 'approve') {
        captureBeerIngestionReviewApprove(run.id, breweryId, candidateKey);
      } else if (action === 'reject') {
        captureBeerIngestionReviewReject(run.id, breweryId, candidateKey);
      } else if (action === 'acknowledge') {
        captureBeerIngestionReviewAcknowledge(run.id, breweryId, candidateKey);
      }
      onReviewUpdated?.(result.run, result.review);
    } catch (decisionError) {
      captureBeerIngestionReviewError(run.id, breweryId);
      setActionError(decisionError?.message || 'Failed to save review decision');
    } finally {
      decisionInFlightKeyRef.current = null;
      setLoadingCandidateKey(null);
      setLoadingDecisionAction('');
    }
  }, [
    beerStyleIds,
    breweryId,
    candidates,
    decisions,
    drafts,
    onReviewUpdated,
    reviewLocked,
    run,
    sessionStyleTypes,
    styleMapping,
  ]);

  const handleDecisionClick = useCallback((event) => {
    const candidateKey = event.currentTarget.getAttribute('data-candidate-key');
    const action = event.currentTarget.getAttribute('data-action');
    if (candidateKey && action) {
      persistDecision(candidateKey, action);
    }
  }, [persistDecision]);

  const handleBulkApprove = useCallback(async () => {
    if (
      !run
      || reviewLocked
      || isReviewMutationInFlight(
        actionInFlightRef,
        decisionInFlightKeyRef,
        styleAssignInFlightRef,
      )
    ) {
      return;
    }
    const keysToApprove = bulkEligibleKeys.filter((candidateKey) => selectedKeys[candidateKey]);
    if (keysToApprove.length === 0) {
      return;
    }
    actionInFlightRef.current = true;
    setActionLoading(true);
    setActionError(null);
    try {
      const result = await bulkApproveIngestion({
        runId: run.id,
        candidateKeys: keysToApprove,
        mappedStyleNames: mappedStyleNamesForBulk(
          candidates,
          styleMapping,
          beerStyleIds,
          sessionStyleTypes,
        ),
      });
      captureBeerIngestionReviewBulk(
        run.id,
        breweryId,
        result.approvedKeys.length,
        result.skippedKeys.length,
      );
      setSelectedKeys({});
      onReviewUpdated?.(result.run, result.review);
    } catch (bulkError) {
      captureBeerIngestionReviewError(run.id, breweryId);
      setActionError(bulkError?.message || 'Failed to bulk approve candidates');
    } finally {
      actionInFlightRef.current = false;
      setActionLoading(false);
    }
  }, [
    beerStyleIds,
    breweryId,
    bulkEligibleKeys,
    candidates,
    onReviewUpdated,
    reviewLocked,
    run,
    selectedKeys,
    sessionStyleTypes,
    styleMapping,
  ]);

  const handleBulkReject = useCallback(async () => {
    if (
      !run
      || reviewLocked
      || isReviewMutationInFlight(
        actionInFlightRef,
        decisionInFlightKeyRef,
        styleAssignInFlightRef,
      )
    ) {
      return;
    }
    const keysToReject = rejectableKeys.filter((candidateKey) => selectedKeys[candidateKey]);
    if (keysToReject.length === 0) {
      return;
    }
    actionInFlightRef.current = true;
    setActionLoading(true);
    setActionError(null);
    try {
      const result = await bulkRejectIngestion({
        runId: run.id,
        candidateKeys: keysToReject,
      });
      captureBeerIngestionReviewBulkReject(
        run.id,
        breweryId,
        result.rejectedKeys.length,
        result.skippedKeys.length,
      );
      setSelectedKeys({});
      onReviewUpdated?.(result.run, result.review);
    } catch (bulkError) {
      captureBeerIngestionReviewError(run.id, breweryId);
      setActionError(bulkError?.message || 'Failed to bulk reject candidates');
    } finally {
      actionInFlightRef.current = false;
      setActionLoading(false);
    }
  }, [
    breweryId,
    onReviewUpdated,
    rejectableKeys,
    reviewLocked,
    run,
    selectedKeys,
  ]);

  const handleComplete = useCallback(async () => {
    if (
      !run
      || reviewLocked
      || isReviewMutationInFlight(
        actionInFlightRef,
        decisionInFlightKeyRef,
        styleAssignInFlightRef,
      )
    ) {
      return;
    }
    if (unmappedApprovedStyles.length > 0) {
      setActionError(unmappedStylesMessage);
      return;
    }
    actionInFlightRef.current = true;
    setActionLoading(true);
    setActionError(null);
    try {
      const result = await completeIngestionReview(run.id);
      captureBeerIngestionReviewComplete(run.id, breweryId);
      onReviewUpdated?.(result.run, result.review);
    } catch (completeError) {
      captureBeerIngestionReviewError(run.id, breweryId);
      setActionError(completeError?.message || 'Failed to complete review');
    } finally {
      actionInFlightRef.current = false;
      setActionLoading(false);
    }
  }, [
    breweryId,
    onReviewUpdated,
    reviewLocked,
    run,
    unmappedApprovedStyles,
    unmappedStylesMessage,
  ]);

  const handleApply = useCallback(async () => {
    if (
      !run
      || run.applyStatus === 'applied'
      || reviewApplied
      || (reviewLocked && !applyRetryable)
      || isReviewMutationInFlight(
        actionInFlightRef,
        decisionInFlightKeyRef,
        styleAssignInFlightRef,
      )
    ) {
      return;
    }
    if (unmappedApprovedStyles.length > 0) {
      setActionError(unmappedStylesMessage);
      return;
    }
    actionInFlightRef.current = true;
    setActionLoading(true);
    setApplying(true);
    setActionError(null);
    try {
      if (!reviewCompleted) {
        const completeResult = await completeIngestionReview(run.id);
        captureBeerIngestionReviewComplete(run.id, breweryId);
        onReviewUpdated?.(completeResult.run, completeResult.review);
      }
      const result = await applyIngestionReview(run.id);
      captureBeerIngestionReviewApply(
        run.id,
        breweryId,
        result.importResults?.addedCount ?? 0,
        result.importResults?.updatedCount ?? 0,
        result.importResults?.duplicatedCount ?? 0,
        result.importResults?.errorCount ?? 0,
      );
      setApplyResult({
        runId: run.id,
        review: result.review,
        importResults: result.importResults,
      });
      const completedApplyStatus = result.applyStatus ?? result.review?.applyStatus;
      if (hasUnresolvedApplyErrors(completedApplyStatus)) {
        setSelectedFilter(COMPARISON_FILTER_APPLY_ERRORS);
      } else if (completedApplyStatus === 'applied') {
        onApplySuccess?.(result.run, result.review);
      }
      onReviewUpdated?.(result.run, result.review);
    } catch (applyError) {
      captureBeerIngestionReviewError(run.id, breweryId);
      setActionError(applyError?.message || 'Failed to apply approved changes');
    } finally {
      actionInFlightRef.current = false;
      setActionLoading(false);
      setApplying(false);
    }
  }, [
    applyRetryable,
    breweryId,
    onApplySuccess,
    onReviewUpdated,
    reviewApplied,
    reviewCompleted,
    reviewLocked,
    run,
    unmappedApprovedStyles,
    unmappedStylesMessage,
  ]);

  const getDraft = useCallback((candidate) => (
    drafts[candidate.candidateKey] ?? proposedBeerDraftFromCandidate(candidate)
  ), [drafts]);

  const getStyleTypeCaption = useCallback((styleLabel) => (
    styleTypeCaptionForLabel(styleLabel, styleMapping, beerStyles, sessionStyleTypes)
  ), [beerStyles, sessionStyleTypes, styleMapping]);

  const isStyleMapped = useCallback((styleLabel) => (
    isExtractedStyleMapped(styleLabel, styleMapping, beerStyleIds, sessionStyleTypes)
  ), [beerStyleIds, sessionStyleTypes, styleMapping]);

  return {
    filter,
    decisions,
    visibleCandidates,
    bulkEligibleKeys,
    rejectableKeys,
    selectableKeys,
    selectedKeys,
    selectedCount,
    selectedBulkEligibleCount,
    selectedRejectableCount,
    editingKeys,
    headerCheckboxState,
    unknownStyles,
    unmappedApprovedStyles,
    unmappedStylesMessage,
    styleTypes,
    reviewLocked,
    reviewApplied,
    applyBlocked,
    applyStatus,
    applyRetryable,
    approvedCount,
    pendingCount,
    decidedCount,
    actionError,
    actionLoading,
    loadingCandidateKey,
    loadingDecisionAction,
    applying,
    assigningStyleName,
    assigningStyleType,
    importResults,
    handleFilterChange,
    handleToggleSelected,
    handleToggleSelectAll,
    handleSelectEligible,
    handleDraftChange,
    handleToggleRowEdit,
    handleAssignStyleType,
    handleDecisionClick,
    handleBulkApprove,
    handleBulkReject,
    handleComplete,
    handleApply,
    getDraft,
    getStyleTypeCaption,
    isStyleMapped,
  };
}
